import { z } from 'zod';

import { normalizeName } from '@/common/event';
import { danceSchema } from '@/common/dance';

const countrySchema = z.object({
	code: z.string(),
	count: z.number(),
});

export const linkSchema = z.object({
	kind: z.string(),
	url: z.string(),
	title: z.string().optional(),
});

export const facebookIdSchema = z.string();

// TODO: Remove .nullish().transform() after we push the new events.json to prod
export const venueSchema = z.object({
	name: z.string().optional().nullish().transform(v => v === null ? undefined : v),
	address: z.string().optional().nullish().transform(v => v === null ? undefined : v),
	url: z.string().optional().nullish().transform(v => v === null ? undefined : v),
});

export const facebookSchema = z.object({
	going: z.number().optional(),
	responded: z.number().optional(),
});

export const ydbSchema = z.object({
	url: z.string(),
	going: z.number().optional(),
	experiences: z.number().optional(),
});

export const westiehubSchema = z.object({
	slug: z.string(),
	attending: z.number().optional(),
});

export const danceCircuitSchema = z.object({
	url: z.string(),
	going: z.number().optional(),
});

export type WestieHub = z.infer<typeof westiehubSchema>;

export const registrationSchema = z.discriminatedUnion('status', [
	z.object({ status: z.literal('pending'), opens_at: z.string(), link: z.string().optional() }),
	z.object({ status: z.literal('open'), link: z.string().optional() }),
	z.object({ status: z.literal('sold_out'), link: z.string().optional() }),
]);

export type Registration = z.infer<typeof registrationSchema>;

export const sundaySchema = z.templateLiteral([z.number(), z.literal('-'), z.number(), z.literal('-'), z.number()]);

export type Sunday = z.infer<typeof sundaySchema>;

export const eventSchema = z.object({
	name: z.string(),
	sunday: sundaySchema,
	sources: z.array(z.string()).optional(),
	cover: z.string().optional(),
	country_code: z.string().optional(),
	hotel: z.boolean().optional(),
	wsdc: z.boolean().optional(),
	nasde: z.boolean().optional(),
	facebook: z.record(facebookIdSchema, facebookSchema).optional(),
	ydb: z.array(ydbSchema).optional(),
	westiehub: z.array(westiehubSchema).optional(),
	dancecircuit: z.array(danceCircuitSchema).optional(),
	links: z.array(linkSchema).optional(),
	people: z.array(z.string()).optional(),
	venues: z.record(z.string(), venueSchema).optional(),
	location: venueSchema.optional(),
	cancelled: z.boolean().optional().transform(v => v === false ? undefined : v),
	registration: registrationSchema.optional(),
	dances: z.array(danceSchema).optional(),
	series: z.string().optional(),
});

export type Event = z.infer<typeof eventSchema>;

export const outputSchema = z.object({
	events: z.array(eventSchema),
	countries: z.array(countrySchema),
	people: z.array(z.string()),
});

export type Output = z.infer<typeof outputSchema>;

export type Venue = z.infer<typeof venueSchema>;
export type Link = z.infer<typeof linkSchema>;

export function eventKey(event: Event): string {
	return `${event.sunday}-${normalizeName(event.name)}`;
}
